export interface PortfolioProject {
  slug: string;
  title: string;
  industry: string;
  category: string;
  categoryKey: string;
  categoryKeys: string[];
  tags: string[];
  description: string;
  image: string;
}

export interface CaseStudy {
  slug: string;
  title: string;
  industry: string;
  badge: string;
  heroImage: string;
  overview: string;
  challenge: string;
  goals: string[];
  solution: string;
  architecture: { name: string; desc: string; icon: string }[];
  keyFeatures: { title: string; desc: string; icon: string }[];
  techStack: { name: string; logo?: string; icon?: string }[];
  process: { title: string; desc: string }[];
  results: { title: string; desc: string; icon: string }[];
  screenshots: string[];
}

export const portfolioFilters = [
  { key: "all", label: "All" },
  { key: "laravel", label: "Laravel" },
  { key: "web", label: "Web" },
  { key: "saas", label: "SaaS" },
  { key: "mobile", label: "Mobile" },
  { key: "api", label: "API" },
  { key: "ai", label: "AI" },
  { key: "enterprise", label: "Enterprise" },
];

export const portfolioProjects: PortfolioProject[] = [
  {
    slug: "enterprise-operations-platform",
    title: "Enterprise Operations Platform",
    industry: "Enterprise",
    category: "Enterprise",
    categoryKey: "enterprise",
    categoryKeys: ["enterprise"],
    tags: ["Laravel", "React", "PostgreSQL", "AWS"],
    description: "A centralized operations platform connecting multiple departments with automated workflows and role-based access.",
    image: "https://readdy.ai/api/search-image?query=Modern%20enterprise%20operations%20dashboard%20UI%2C%20workflow%20management%20interface%20with%20charts%20and%20process%20panels%2C%20professional%20enterprise%20software%20mockup%2C%20navy%20and%20blue%20gradient%20lighting%2C%20premium%20SaaS%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-enterprise-ops&orientation=landscape",
  },
  {
    slug: "ai-powered-saas-platform",
    title: "AI-Powered SaaS Platform",
    industry: "SaaS",
    category: "AI / SaaS",
    categoryKey: "ai",
    categoryKeys: ["ai", "saas"],
    tags: ["Laravel", "AI", "Claude", "React"],
    description: "A multi-tenant SaaS platform with AI assistants and workflow automation built for modern teams.",
    image: "https://readdy.ai/api/search-image?query=Modern%20AI-powered%20SaaS%20dashboard%20UI%2C%20assistant%20chat%20panel%20with%20automation%20workflows%2C%20intelligent%20software%20interface%20mockup%2C%20purple%20and%20pink%20gradient%20lighting%2C%20premium%20AI%20SaaS%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-ai-saas&orientation=landscape",
  },
  {
    slug: "healthcare-management-system",
    title: "Healthcare Management System",
    industry: "Healthcare",
    category: "Web",
    categoryKey: "web",
    categoryKeys: ["web"],
    tags: ["Laravel", "React", "MySQL", "AWS"],
    description: "A secure patient management platform with appointments, records and telehealth features.",
    image: "https://readdy.ai/api/search-image?query=Modern%20healthcare%20management%20dashboard%20UI%2C%20patient%20records%20and%20appointment%20interface%2C%20professional%20medical%20software%20mockup%2C%20blue%20and%20teal%20gradient%20lighting%2C%20premium%20healthtech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-healthcare&orientation=landscape",
  },
  {
    slug: "fintech-dashboard",
    title: "FinTech Dashboard",
    industry: "FinTech",
    category: "Web / Enterprise",
    categoryKey: "web",
    categoryKeys: ["web", "enterprise"],
    tags: ["Laravel", "React", "PostgreSQL", "Stripe"],
    description: "A real-time financial dashboard with transaction monitoring, analytics and secure reporting.",
    image: "https://readdy.ai/api/search-image?query=Modern%20fintech%20analytics%20dashboard%20UI%2C%20transaction%20charts%20and%20financial%20graphs%2C%20secure%20banking%20interface%20mockup%2C%20blue%20and%20purple%20gradient%20lighting%2C%20premium%20fintech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-fintech&orientation=landscape",
  },
  {
    slug: "ecommerce-platform",
    title: "E-commerce Platform",
    industry: "E-commerce",
    category: "Web",
    categoryKey: "web",
    categoryKeys: ["web"],
    tags: ["Laravel", "Vue.js", "MySQL", "Stripe"],
    description: "A high-performance commerce platform with product catalogs, payments and order management.",
    image: "https://readdy.ai/api/search-image?query=Modern%20e-commerce%20storefront%20UI%2C%20product%20grid%20with%20shopping%20cart%20interface%2C%20online%20store%20mockup%2C%20blue%20and%20orange%20gradient%20lighting%2C%20premium%20commerce%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-ecommerce&orientation=landscape",
  },
  {
    slug: "logistics-management-system",
    title: "Logistics Management System",
    industry: "Logistics",
    category: "Mobile / Enterprise",
    categoryKey: "enterprise",
    categoryKeys: ["enterprise", "mobile"],
    tags: ["Laravel", "Flutter", "PostgreSQL", "AWS"],
    description: "A fleet and delivery management platform with real-time tracking and route optimization.",
    image: "https://readdy.ai/api/search-image?query=Modern%20logistics%20management%20dashboard%20UI%2C%20fleet%20tracking%20map%20with%20delivery%20routes%2C%20supply%20chain%20software%20mockup%2C%20blue%20and%20orange%20gradient%20lighting%2C%20premium%20logistics%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-logistics&orientation=landscape",
  },
  {
    slug: "education-platform",
    title: "Education Platform",
    industry: "Education",
    category: "SaaS / Web",
    categoryKey: "saas",
    categoryKeys: ["saas", "web"],
    tags: ["Laravel", "Vue.js", "Flutter", "MySQL"],
    description: "An online learning platform with courses, assessments and a companion mobile app.",
    image: "https://readdy.ai/api/search-image?query=Modern%20education%20platform%20UI%2C%20course%20cards%20and%20learning%20dashboard%20interface%2C%20EdTech%20software%20mockup%2C%20blue%20and%20green%20gradient%20lighting%2C%20premium%20EdTech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-education&orientation=landscape",
  },
  {
    slug: "mobile-business-application",
    title: "Mobile Business Application",
    industry: "Business",
    category: "Mobile",
    categoryKey: "mobile",
    categoryKeys: ["mobile"],
    tags: ["Flutter", "Dart", "Laravel", "API"],
    description: "A cross-platform business app with real-time features, notifications and Laravel API integration.",
    image: "https://readdy.ai/api/search-image?query=Modern%20business%20mobile%20app%20UI%2C%20smartphone%20with%20dashboard%20screens%20and%20charts%2C%20cross-platform%20app%20mockup%2C%20blue%20and%20cyan%20gradient%20lighting%2C%20premium%20mobile%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-mobile-business&orientation=landscape",
  },
  {
    slug: "ai-customer-support-assistant",
    title: "AI Customer Support Assistant",
    industry: "AI",
    category: "AI / API",
    categoryKey: "ai",
    categoryKeys: ["ai", "api"],
    tags: ["Laravel", "AI", "Redis", "AWS"],
    description: "An AI-powered support assistant that automates responses, triages tickets and learns from every customer conversation.",
    image: "https://readdy.ai/api/search-image?query=AI%20customer%20support%20assistant%20interface%2C%20chat%20bubbles%20with%20automated%20reply%20suggestions%20and%20ticket%20queue%20panel%2C%20intelligent%20helpdesk%20software%20mockup%2C%20violet%20and%20magenta%20gradient%20lighting%2C%20premium%20AI%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-ai-support&orientation=landscape",
  },
  {
    slug: "business-crm-platform",
    title: "Business CRM Platform",
    industry: "SaaS",
    category: "Laravel / SaaS",
    categoryKey: "laravel",
    categoryKeys: ["laravel", "saas"],
    tags: ["Laravel", "Vue.js", "MySQL", "Redis"],
    description: "A flexible CRM for managing leads, contacts and pipelines with automated follow-ups and sales analytics.",
    image: "https://readdy.ai/api/search-image?query=CRM%20platform%20dashboard%20UI%2C%20sales%20pipeline%20kanban%20board%20with%20lead%20cards%20and%20contact%20profiles%2C%20professional%20business%20software%20mockup%2C%20teal%20and%20orange%20gradient%20lighting%2C%20premium%20SaaS%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-crm&orientation=landscape",
  },
  {
    slug: "api-integration-platform",
    title: "API Integration Platform",
    industry: "API",
    category: "API",
    categoryKey: "api",
    categoryKeys: ["api"],
    tags: ["Laravel", "REST API", "PostgreSQL", "Docker"],
    description: "A developer-friendly integration platform to build, secure and monitor REST APIs and connect third-party services.",
    image: "https://readdy.ai/api/search-image?query=API%20integration%20platform%20UI%2C%20endpoint%20explorer%20with%20request%20and%20response%20panels%20and%20connection%20flow%20diagram%2C%20developer%20tools%20software%20mockup%2C%20blue%20and%20violet%20gradient%20lighting%2C%20premium%20developer%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-api&orientation=landscape",
  },
  {
    slug: "enterprise-analytics-dashboard",
    title: "Enterprise Analytics Dashboard",
    industry: "Enterprise",
    category: "Enterprise / Web",
    categoryKey: "enterprise",
    categoryKeys: ["enterprise", "web"],
    tags: ["Laravel", "React", "PostgreSQL", "AWS"],
    description: "A real-time business intelligence dashboard with interactive charts, reports and KPI monitoring for leadership teams.",
    image: "https://readdy.ai/api/search-image?query=Enterprise%20analytics%20dashboard%20UI%2C%20interactive%20KPI%20cards%20and%20business%20intelligence%20charts%2C%20executive%20reporting%20software%20mockup%2C%20navy%20and%20gold%20gradient%20lighting%2C%20premium%20enterprise%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=800&height=560&seq=port-analytics&orientation=landscape",
  },
];

export const caseStudies: CaseStudy[] = [
  {
    slug: "enterprise-operations-platform",
    title: "Enterprise Operations Platform",
    industry: "Enterprise",
    badge: "Case Study · Enterprise",
    heroImage: "https://readdy.ai/api/search-image?query=Modern%20enterprise%20operations%20dashboard%20UI%2C%20workflow%20management%20interface%20with%20charts%20and%20process%20panels%2C%20professional%20enterprise%20software%20mockup%2C%20navy%20and%20blue%20gradient%20lighting%2C%20premium%20SaaS%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-enterprise-ops&orientation=landscape",
    overview:
      "An established organization needed to replace fragmented tools and manual processes with a single, centralized operations platform. AstroJal engineered a scalable web application connecting multiple departments with automated workflows, role-based access and unified data.",
    challenge:
      "The organization operated across several disconnected systems, leading to duplicated data, slow approvals and limited visibility. They needed one platform to unify operations, automate repetitive workflows and provide leadership with real-time insight — without disrupting existing teams.",
    goals: [
      "Centralize data from multiple departments",
      "Automate manual approval workflows",
      "Provide role-based access and security",
      "Deliver real-time operational dashboards",
      "Integrate with existing systems",
    ],
    solution:
      "We built a Laravel-powered operations platform with a React frontend. A centralized data layer unifies departments, while an automated workflow engine streamlines approvals and task routing. Robust role-based access keeps data secure, and live dashboards give leadership real-time visibility across the business.",
    architecture: [
      { name: "Frontend", desc: "React SPA", icon: "ri-window-line" },
      { name: "Laravel API", desc: "Business logic", icon: "ri-code-s-slash-line" },
      { name: "Business Logic", desc: "Workflow engine", icon: "ri-settings-3-line" },
      { name: "Redis", desc: "Cache & queues", icon: "ri-stack-line" },
      { name: "Database", desc: "PostgreSQL", icon: "ri-database-2-line" },
      { name: "AWS", desc: "Cloud infrastructure", icon: "ri-cloud-line" },
    ],
    keyFeatures: [
      { title: "Automated Workflows", desc: "Streamlined approvals and task routing.", icon: "ri-settings-3-line" },
      { title: "Role-Based Access", desc: "Secure permissions per department.", icon: "ri-shield-check-line" },
      { title: "Real-time Dashboards", desc: "Live operational visibility.", icon: "ri-line-chart-line" },
      { title: "Centralized Data", desc: "One source of truth.", icon: "ri-database-2-line" },
      { title: "Modern UI", desc: "Clean, intuitive interface.", icon: "ri-palette-line" },
      { title: "System Integration", desc: "Connected to existing tools.", icon: "ri-links-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Mapped departmental workflows and pain points." },
      { title: "Architecture", desc: "Designed a centralized data and workflow model." },
      { title: "UI/UX", desc: "Built clean dashboards for every role." },
      { title: "Development", desc: "Delivered modules in agile sprints." },
      { title: "Testing", desc: "Automated and manual QA throughout." },
      { title: "Deployment", desc: "CI/CD pipeline on AWS." },
      { title: "Support", desc: "Ongoing monitoring and iteration." },
    ],
    results: [
      { title: "Scalable Architecture", desc: "Designed to grow with the organization.", icon: "ri-expand-diagonal-line" },
      { title: "Modern UI", desc: "Clean, intuitive experience for all users.", icon: "ri-palette-line" },
      { title: "Automated Workflows", desc: "Faster approvals and fewer errors.", icon: "ri-settings-3-line" },
      { title: "Centralized Data", desc: "Single source of truth across teams.", icon: "ri-database-2-line" },
      { title: "Improved UX", desc: "Better tooling for every department.", icon: "ri-user-smile-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=Enterprise%20operations%20dashboard%20with%20workflow%20panels%20and%20KPI%20charts%2C%20clean%20professional%20software%20UI%2C%20navy%20and%20blue%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-ops-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Enterprise%20workflow%20automation%20interface%20with%20approval%20flow%20and%20task%20list%2C%20clean%20professional%20software%20UI%2C%20navy%20and%20blue%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-ops-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Enterprise%20analytics%20dashboard%20with%20department%20charts%20and%20reporting%2C%20clean%20professional%20software%20UI%2C%20navy%20and%20blue%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-ops-3&orientation=landscape",
    ],
  },
  {
    slug: "ai-powered-saas-platform",
    title: "AI-Powered SaaS Platform",
    industry: "SaaS",
    badge: "Case Study · AI SaaS",
    heroImage: "https://readdy.ai/api/search-image?query=Modern%20AI-powered%20SaaS%20dashboard%20UI%2C%20assistant%20chat%20panel%20with%20automation%20workflows%2C%20intelligent%20software%20interface%20mockup%2C%20purple%20and%20pink%20gradient%20lighting%2C%20premium%20AI%20SaaS%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-ai-saas&orientation=landscape",
    overview:
      "A fast-growing SaaS company wanted to add intelligent, automated workflows to its platform. AstroJal integrated AI assistants and automation into a multi-tenant Laravel SaaS product, delivering smarter features without disrupting the existing architecture.",
    challenge:
      "The platform's manual workflows created bottlenecks, and the team wanted AI-powered assistance to help users work faster. They needed AI integrated securely and scalably, with multi-tenancy preserved and no disruption to existing users.",
    goals: [
      "Integrate AI assistants into the product",
      "Automate repetitive user workflows",
      "Preserve multi-tenant architecture",
      "Keep AI features secure and configurable",
      "Ship without disrupting existing users",
    ],
    solution:
      "We extended the Laravel SaaS platform with an AI integration layer that routes requests to language models. Users now access AI assistants and automated workflows directly in the product, with per-tenant configuration and secure, audited calls.",
    architecture: [
      { name: "SaaS Frontend", desc: "React / Vue UI", icon: "ri-window-line" },
      { name: "Laravel API", desc: "Business logic", icon: "ri-code-s-slash-line" },
      { name: "AI Integration Layer", desc: "Prompt orchestration", icon: "ri-bard-line" },
      { name: "AI Models", desc: "ChatGPT / Claude", icon: "ri-bubble-chart-line" },
      { name: "Database", desc: "User context", icon: "ri-database-2-line" },
      { name: "Cloud", desc: "Secure deployment", icon: "ri-cloud-line" },
    ],
    keyFeatures: [
      { title: "AI Assistants", desc: "In-product intelligent help.", icon: "ri-robot-line" },
      { title: "Workflow Automation", desc: "Automate repetitive tasks.", icon: "ri-settings-3-line" },
      { title: "Multi-tenancy", desc: "Per-tenant AI configuration.", icon: "ri-building-4-line" },
      { title: "Secure Calls", desc: "Audited, protected AI requests.", icon: "ri-shield-check-line" },
      { title: "Scalable", desc: "AI that grows with usage.", icon: "ri-expand-diagonal-line" },
      { title: "Modern UI", desc: "Seamless integrated experience.", icon: "ri-palette-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "ChatGPT", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/openai.svg" },
      { name: "Claude", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/claude-color.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Identified high-impact automation workflows." },
      { title: "Architecture", desc: "Designed a secure AI integration layer." },
      { title: "UI/UX", desc: "Built seamless in-product AI experiences." },
      { title: "Development", desc: "Integrated AI across tenant features." },
      { title: "Testing", desc: "Validated security and scalability." },
      { title: "Deployment", desc: "Rolled out without downtime." },
      { title: "Support", desc: "Ongoing AI refinement." },
    ],
    results: [
      { title: "Automation", desc: "Users complete tasks faster.", icon: "ri-settings-3-line" },
      { title: "Intelligent Features", desc: "AI assistants in the product.", icon: "ri-bard-line" },
      { title: "Scalable", desc: "AI grows with the platform.", icon: "ri-expand-diagonal-line" },
      { title: "Modern UX", desc: "Smoother user experience.", icon: "ri-user-smile-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=AI%20SaaS%20dashboard%20with%20assistant%20chat%20panel%20and%20automation%20cards%2C%20clean%20professional%20software%20UI%2C%20purple%20and%20pink%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-ai-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=AI%20workflow%20automation%20interface%20with%20task%20suggestions%20and%20chat%2C%20clean%20professional%20software%20UI%2C%20purple%20and%20pink%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-ai-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Multi-tenant%20SaaS%20admin%20panel%20with%20AI%20settings%2C%20clean%20professional%20software%20UI%2C%20purple%20and%20pink%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-ai-3&orientation=landscape",
    ],
  },
  {
    slug: "healthcare-management-system",
    title: "Healthcare Management System",
    industry: "Healthcare",
    badge: "Case Study · Healthcare",
    heroImage: "https://readdy.ai/api/search-image?query=Modern%20healthcare%20management%20dashboard%20UI%2C%20patient%20records%20and%20appointment%20interface%2C%20professional%20medical%20software%20mockup%2C%20blue%20and%20teal%20gradient%20lighting%2C%20premium%20healthtech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-health&orientation=landscape",
    overview:
      "A growing healthcare network needed a secure platform to manage patients, appointments and records. AstroJal built a comprehensive healthcare management system with strong security, role-based access and a clean patient experience.",
    challenge:
      "Managing patients across multiple locations created scattered records, missed appointments and manual admin work. The network needed a secure, centralized system that staff and patients could both use, with strict data protection.",
    goals: [
      "Centralize patient records",
      "Streamline appointment booking",
      "Provide secure role-based access",
      "Deliver a clean patient portal",
      "Ensure strong data protection",
    ],
    solution:
      "We built a Laravel-based healthcare management system with a secure React frontend. Patients book appointments and view records through a portal, while staff manage schedules and records with granular permissions — all protected by strong security and audit controls.",
    architecture: [
      { name: "Patient Portal", desc: "Web & mobile", icon: "ri-window-line" },
      { name: "Laravel API", desc: "Business logic", icon: "ri-code-s-slash-line" },
      { name: "Records Service", desc: "Secure data layer", icon: "ri-file-list-line" },
      { name: "Redis", desc: "Cache & queues", icon: "ri-stack-line" },
      { name: "Database", desc: "MySQL", icon: "ri-database-2-line" },
      { name: "AWS", desc: "Secure cloud", icon: "ri-cloud-line" },
    ],
    keyFeatures: [
      { title: "Patient Records", desc: "Secure centralized records.", icon: "ri-file-list-line" },
      { title: "Appointments", desc: "Online booking and reminders.", icon: "ri-calendar-line" },
      { title: "Role-Based Access", desc: "Granular staff permissions.", icon: "ri-shield-check-line" },
      { title: "Patient Portal", desc: "Self-service for patients.", icon: "ri-user-line" },
      { title: "Data Security", desc: "Encryption and audit logs.", icon: "ri-lock-2-line" },
      { title: "Analytics", desc: "Operational insights.", icon: "ri-line-chart-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "REST API", icon: "ri-exchange-line" },
    ],
    process: [
      { title: "Discovery", desc: "Mapped clinical and admin workflows." },
      { title: "Architecture", desc: "Designed a secure data model." },
      { title: "UI/UX", desc: "Built intuitive staff and patient views." },
      { title: "Development", desc: "Delivered modules in sprints." },
      { title: "Testing", desc: "Security and compliance QA." },
      { title: "Deployment", desc: "Secure AWS rollout." },
      { title: "Support", desc: "Ongoing maintenance." },
    ],
    results: [
      { title: "Better Patient Care", desc: "Streamlined workflows.", icon: "ri-heart-pulse-line" },
      { title: "Reduced Admin", desc: "Less manual work.", icon: "ri-settings-3-line" },
      { title: "Data Security", desc: "Protected records.", icon: "ri-shield-check-line" },
      { title: "Scalable", desc: "Grows with the network.", icon: "ri-expand-diagonal-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=Healthcare%20dashboard%20with%20patient%20appointments%20and%20records%2C%20clean%20professional%20medical%20software%20UI%2C%20blue%20and%20teal%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-health-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Healthcare%20appointment%20booking%20interface%20with%20calendar%2C%20clean%20professional%20medical%20software%20UI%2C%20blue%20and%20teal%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-health-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Healthcare%20patient%20portal%20dashboard%2C%20clean%20professional%20medical%20software%20UI%2C%20blue%20and%20teal%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-health-3&orientation=landscape",
    ],
  },
  {
    slug: "fintech-dashboard",
    title: "FinTech Dashboard",
    industry: "FinTech",
    badge: "Case Study · FinTech",
    heroImage: "https://readdy.ai/api/search-image?query=Modern%20fintech%20analytics%20dashboard%20UI%2C%20transaction%20charts%20and%20financial%20graphs%2C%20secure%20banking%20interface%20mockup%2C%20blue%20and%20purple%20gradient%20lighting%2C%20premium%20fintech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-fintech&orientation=landscape",
    overview:
      "A financial services company needed a real-time dashboard to monitor transactions and performance. AstroJal built a secure, data-rich fintech dashboard with live reporting and robust security.",
    challenge:
      "Stakeholders lacked a single view of transactions and performance, relying on slow manual reports. They needed a real-time, secure dashboard that leadership and analysts could trust, with granular access controls.",
    goals: [
      "Provide real-time transaction visibility",
      "Deliver secure reporting",
      "Build intuitive dashboards",
      "Handle large data volumes",
      "Protect sensitive financial data",
    ],
    solution:
      "We built a React fintech dashboard powered by a Laravel API and PostgreSQL, with Redis caching for speed. Real-time charts and reports give stakeholders instant insight, while role-based access and encryption keep data secure.",
    architecture: [
      { name: "Frontend", desc: "React dashboard", icon: "ri-window-line" },
      { name: "Laravel API", desc: "Reporting & logic", icon: "ri-code-s-slash-line" },
      { name: "Redis", desc: "Caching & speed", icon: "ri-stack-line" },
      { name: "Database", desc: "PostgreSQL", icon: "ri-database-2-line" },
      { name: "Security Layer", desc: "Encryption & access", icon: "ri-shield-check-line" },
      { name: "AWS", desc: "Secure cloud", icon: "ri-cloud-line" },
    ],
    keyFeatures: [
      { title: "Real-time Charts", desc: "Live transaction analytics.", icon: "ri-line-chart-line" },
      { title: "Secure Reporting", desc: "Protected financial data.", icon: "ri-shield-check-line" },
      { title: "Role-Based Access", desc: "Granular permissions.", icon: "ri-user-line" },
      { title: "High Performance", desc: "Fast with large datasets.", icon: "ri-speed-line" },
      { title: "Modern UI", desc: "Clean data visualization.", icon: "ri-palette-line" },
      { title: "Automation", desc: "Scheduled report delivery.", icon: "ri-settings-3-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "Stripe", icon: "ri-bank-card-line" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Identified key metrics and roles." },
      { title: "Architecture", desc: "Designed a scalable reporting model." },
      { title: "UI/UX", desc: "Built clear data dashboards." },
      { title: "Development", desc: "Implemented live reporting." },
      { title: "Testing", desc: "Security and performance QA." },
      { title: "Deployment", desc: "Secure production rollout." },
      { title: "Support", desc: "Ongoing enhancements." },
    ],
    results: [
      { title: "Real-time Insight", desc: "Instant transaction visibility.", icon: "ri-line-chart-line" },
      { title: "Better Decisions", desc: "Data-driven reporting.", icon: "ri-bar-chart-line" },
      { title: "Security", desc: "Protected financial data.", icon: "ri-shield-check-line" },
      { title: "Performance", desc: "Fast with large data.", icon: "ri-speed-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=Fintech%20dashboard%20with%20transaction%20charts%20and%20graphs%2C%20clean%20professional%20financial%20software%20UI%2C%20blue%20and%20purple%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-fin-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Fintech%20analytics%20panel%20with%20revenue%20and%20metrics%2C%20clean%20professional%20financial%20software%20UI%2C%20blue%20and%20purple%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-fin-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Fintech%20secure%20reporting%20interface%20with%20charts%2C%20clean%20professional%20financial%20software%20UI%2C%20blue%20and%20purple%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-fin-3&orientation=landscape",
    ],
  },
  {
    slug: "ecommerce-platform",
    title: "E-commerce Platform",
    industry: "E-commerce",
    badge: "Case Study · E-commerce",
    heroImage: "https://readdy.ai/api/search-image?query=Modern%20e-commerce%20storefront%20UI%2C%20product%20grid%20with%20shopping%20cart%20interface%2C%20online%20store%20mockup%2C%20blue%20and%20orange%20gradient%20lighting%2C%20premium%20commerce%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-commerce&orientation=landscape",
    overview:
      "A retail business needed a scalable, custom e-commerce platform with flexible payments and inventory management. AstroJal built a high-performance store with a smooth checkout and full admin control.",
    challenge:
      "The business outgrew its template store, hitting limits on products, payments and customization. They needed a scalable platform with real-time inventory, flexible payment options and full ownership of the experience.",
    goals: [
      "Build a scalable custom store",
      "Integrate multiple payments",
      "Manage inventory in real time",
      "Deliver a fast checkout",
      "Provide full admin control",
    ],
    solution:
      "We built a Laravel e-commerce platform with a Vue.js storefront. Real-time inventory, multi-gateway payments, order management and a powerful admin panel give the business complete control — built to scale as it grows.",
    architecture: [
      { name: "Storefront", desc: "Vue.js frontend", icon: "ri-window-line" },
      { name: "Laravel API", desc: "Catalog & orders", icon: "ri-code-s-slash-line" },
      { name: "Payment Gateway", desc: "Stripe / PayPal", icon: "ri-bank-card-line" },
      { name: "Redis", desc: "Cache & sessions", icon: "ri-stack-line" },
      { name: "Database", desc: "MySQL", icon: "ri-database-2-line" },
      { name: "Cloud", desc: "AWS / CDN", icon: "ri-cloud-line" },
    ],
    keyFeatures: [
      { title: "Product Catalog", desc: "Scalable products and variants.", icon: "ri-shopping-bag-line" },
      { title: "Multi-Payment", desc: "Flexible checkout options.", icon: "ri-bank-card-line" },
      { title: "Order Management", desc: "Full order lifecycle.", icon: "ri-file-list-line" },
      { title: "Inventory", desc: "Real-time stock tracking.", icon: "ri-archive-line" },
      { title: "Admin Panel", desc: "Full store control.", icon: "ri-settings-3-line" },
      { title: "Analytics", desc: "Sales and customer insight.", icon: "ri-line-chart-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "Stripe", icon: "ri-bank-card-line" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Reviewed catalog and checkout needs." },
      { title: "Architecture", desc: "Designed a scalable commerce model." },
      { title: "UI/UX", desc: "Built a conversion-focused store." },
      { title: "Development", desc: "Implemented payments and inventory." },
      { title: "Testing", desc: "Checkout and performance QA." },
      { title: "Deployment", desc: "Production launch on AWS." },
      { title: "Support", desc: "Ongoing optimization." },
    ],
    results: [
      { title: "Scalable", desc: "Grows with product range.", icon: "ri-expand-diagonal-line" },
      { title: "Faster Checkout", desc: "Optimized purchase flow.", icon: "ri-speed-line" },
      { title: "Full Control", desc: "Own the store experience.", icon: "ri-settings-3-line" },
      { title: "Modern UI", desc: "Premium storefront.", icon: "ri-palette-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=E-commerce%20storefront%20with%20product%20grid%20and%20filters%2C%20clean%20professional%20commerce%20software%20UI%2C%20blue%20and%20orange%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-commerce-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=E-commerce%20checkout%20interface%20with%20payment%20options%2C%20clean%20professional%20commerce%20software%20UI%2C%20blue%20and%20orange%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-commerce-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=E-commerce%20admin%20dashboard%20with%20orders%20and%20inventory%2C%20clean%20professional%20commerce%20software%20UI%2C%20blue%20and%20orange%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-commerce-3&orientation=landscape",
    ],
  },
  {
    slug: "logistics-management-system",
    title: "Logistics Management System",
    industry: "Logistics",
    badge: "Case Study · Logistics",
    heroImage: "https://readdy.ai/api/search-image?query=Modern%20logistics%20management%20dashboard%20UI%2C%20fleet%20tracking%20map%20with%20delivery%20routes%2C%20supply%20chain%20software%20mockup%2C%20blue%20and%20orange%20gradient%20lighting%2C%20premium%20logistics%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-logistics&orientation=landscape",
    overview:
      "A logistics company needed better visibility over its fleet and deliveries. AstroJal built a tracking and management platform with real-time maps, route tools and companion mobile apps.",
    challenge:
      "Manual dispatching and limited visibility caused delays and higher costs. The company needed real-time fleet tracking, better route planning and a clear way for drivers and customers to stay informed.",
    goals: [
      "Enable real-time fleet tracking",
      "Optimize delivery routes",
      "Provide driver and customer apps",
      "Improve dispatch visibility",
      "Reduce delivery costs",
    ],
    solution:
      "We built a Laravel logistics platform with a React dashboard and Flutter mobile apps for drivers and customers. Real-time GPS tracking, route optimization and live delivery updates transform how the fleet operates.",
    architecture: [
      { name: "Dashboard", desc: "React web app", icon: "ri-window-line" },
      { name: "Mobile Apps", desc: "Flutter driver & customer", icon: "ri-smartphone-line" },
      { name: "Laravel API", desc: "Dispatch & logic", icon: "ri-code-s-slash-line" },
      { name: "Real-time Service", desc: "GPS & tracking", icon: "ri-map-2-line" },
      { name: "Database", desc: "PostgreSQL", icon: "ri-database-2-line" },
      { name: "Cloud", desc: "AWS / maps", icon: "ri-cloud-line" },
    ],
    keyFeatures: [
      { title: "GPS Tracking", desc: "Real-time fleet location.", icon: "ri-map-2-line" },
      { title: "Route Optimization", desc: "Smarter delivery routes.", icon: "ri-route-line" },
      { title: "Driver App", desc: "Mobile tools for drivers.", icon: "ri-smartphone-line" },
      { title: "Customer Tracking", desc: "Live delivery updates.", icon: "ri-truck-line" },
      { title: "Dispatch Dashboard", desc: "Central operations view.", icon: "ri-dashboard-line" },
      { title: "Analytics", desc: "Performance insights.", icon: "ri-line-chart-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Mapped fleet and dispatch workflows." },
      { title: "Architecture", desc: "Designed real-time tracking." },
      { title: "UI/UX", desc: "Built dashboard and mobile views." },
      { title: "Development", desc: "Implemented tracking and routes." },
      { title: "Testing", desc: "Real-time and device QA." },
      { title: "Deployment", desc: "Rollout across fleet." },
      { title: "Support", desc: "Ongoing optimization." },
    ],
    results: [
      { title: "Faster Deliveries", desc: "Optimized routes.", icon: "ri-flashlight-line" },
      { title: "Lower Costs", desc: "Efficient operations.", icon: "ri-money-cny-circle-line" },
      { title: "Better Visibility", desc: "Real-time tracking.", icon: "ri-eye-line" },
      { title: "Scalable", desc: "Grows with the fleet.", icon: "ri-expand-diagonal-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=Logistics%20dashboard%20with%20fleet%20tracking%20map%20and%20routes%2C%20clean%20professional%20logistics%20software%20UI%2C%20blue%20and%20orange%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-log-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Logistics%20dispatch%20panel%20with%20delivery%20list%20and%20status%2C%20clean%20professional%20logistics%20software%20UI%2C%20blue%20and%20orange%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-log-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Logistics%20mobile%20app%20with%20delivery%20route%20and%20tracking%2C%20clean%20professional%20app%20UI%2C%20blue%20and%20orange%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-log-3&orientation=landscape",
    ],
  },
  {
    slug: "education-platform",
    title: "Education Platform",
    industry: "Education",
    badge: "Case Study · Education",
    heroImage: "https://readdy.ai/api/search-image?query=Modern%20education%20platform%20UI%2C%20course%20cards%20and%20learning%20dashboard%20interface%2C%20EdTech%20software%20mockup%2C%20blue%20and%20green%20gradient%20lighting%2C%20premium%20EdTech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-edu&orientation=landscape",
    overview:
      "An education startup needed a complete online learning platform with courses, assessments and a mobile app. AstroJal built a scalable platform serving thousands of learners.",
    challenge:
      "Delivering quality content at scale was difficult with manual processes. The startup needed a platform with video courses, quizzes, progress tracking and a mobile app to grow its student base.",
    goals: [
      "Build an online learning platform",
      "Deliver video courses and quizzes",
      "Track student progress",
      "Provide a mobile app",
      "Scale to many learners",
    ],
    solution:
      "We built a Laravel learning platform with a Vue.js frontend and a Flutter mobile app. Video courses, quizzes, certificates and progress tracking give learners a complete experience while the team manages everything from one admin panel.",
    architecture: [
      { name: "Web Platform", desc: "Vue.js frontend", icon: "ri-window-line" },
      { name: "Mobile App", desc: "Flutter companion", icon: "ri-smartphone-line" },
      { name: "Laravel API", desc: "Courses & users", icon: "ri-code-s-slash-line" },
      { name: "Media", desc: "Video streaming", icon: "ri-movie-line" },
      { name: "Database", desc: "MySQL", icon: "ri-database-2-line" },
      { name: "Cloud", desc: "AWS / CDN", icon: "ri-cloud-line" },
    ],
    keyFeatures: [
      { title: "Video Courses", desc: "Structured learning content.", icon: "ri-video-line" },
      { title: "Quizzes", desc: "Assessments and grading.", icon: "ri-questionnaire-line" },
      { title: "Progress Tracking", desc: "Real-time learner insights.", icon: "ri-line-chart-line" },
      { title: "Certificates", desc: "Automated credentialing.", icon: "ri-award-line" },
      { title: "Mobile App", desc: "Learn anywhere.", icon: "ri-smartphone-line" },
      { title: "Admin Panel", desc: "Full content control.", icon: "ri-settings-3-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Reviewed learning workflows." },
      { title: "Architecture", desc: "Designed content and assessment model." },
      { title: "UI/UX", desc: "Built learner-friendly interfaces." },
      { title: "Development", desc: "Built courses and mobile app." },
      { title: "Testing", desc: "Cross-device QA." },
      { title: "Deployment", desc: "Scalable production launch." },
      { title: "Support", desc: "Ongoing content support." },
    ],
    results: [
      { title: "Scalable", desc: "Serves many learners.", icon: "ri-expand-diagonal-line" },
      { title: "Engaging", desc: "Better learning experience.", icon: "ri-user-smile-line" },
      { title: "Mobile-Ready", desc: "Learn anywhere.", icon: "ri-smartphone-line" },
      { title: "Modern UI", desc: "Clean platform.", icon: "ri-palette-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=Education%20platform%20with%20course%20cards%20and%20learning%20dashboard%2C%20clean%20professional%20EdTech%20software%20UI%2C%20blue%20and%20green%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-edu-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Education%20quiz%20interface%20with%20assessment%20and%20grading%2C%20clean%20professional%20EdTech%20software%20UI%2C%20blue%20and%20green%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-edu-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Education%20mobile%20app%20with%20video%20course%20player%2C%20clean%20professional%20EdTech%20app%20UI%2C%20blue%20and%20green%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-edu-3&orientation=landscape",
    ],
  },
  {
    slug: "mobile-business-application",
    title: "Mobile Business Application",
    industry: "SaaS",
    badge: "Case Study · Mobile",
    heroImage: "https://readdy.ai/api/search-image?query=Modern%20business%20mobile%20app%20UI%2C%20smartphone%20with%20dashboard%20screens%20and%20charts%2C%20cross-platform%20app%20mockup%2C%20blue%20and%20cyan%20gradient%20lighting%2C%20premium%20mobile%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-mobile&orientation=landscape",
    overview:
      "A business needed a cross-platform mobile application for its teams. AstroJal built a Flutter app with real-time features, notifications and a robust Laravel API backend.",
    challenge:
      "Teams needed a fast, unified mobile experience for both iOS and Android. A single native codebase for each platform would be costly and slow, so the business wanted one app that worked everywhere with reliable data and notifications.",
    goals: [
      "Build one app for iOS and Android",
      "Deliver real-time features",
      "Add push notifications",
      "Provide a robust API backend",
      "Ensure a polished mobile UI",
    ],
    solution:
      "We built a Flutter app backed by a Laravel API. Real-time updates, push notifications and a clean, consistent interface give teams a powerful tool on both platforms — all from a single codebase.",
    architecture: [
      { name: "Flutter App", desc: "iOS & Android", icon: "ri-smartphone-line" },
      { name: "REST API", desc: "JSON data layer", icon: "ri-exchange-line" },
      { name: "Laravel Backend", desc: "Business logic", icon: "ri-code-s-slash-line" },
      { name: "Push Service", desc: "Notifications", icon: "ri-notification-3-line" },
      { name: "Database", desc: "PostgreSQL", icon: "ri-database-2-line" },
      { name: "Cloud", desc: "AWS", icon: "ri-cloud-line" },
    ],
    keyFeatures: [
      { title: "Cross-Platform", desc: "One app, two stores.", icon: "ri-smartphone-line" },
      { title: "Real-time", desc: "Live data updates.", icon: "ri-radio-line" },
      { title: "Notifications", desc: "Push engagement.", icon: "ri-notification-3-line" },
      { title: "Secure API", desc: "Robust Laravel backend.", icon: "ri-shield-check-line" },
      { title: "Modern UI", desc: "Polished interface.", icon: "ri-palette-line" },
      { title: "Offline-ready", desc: "Works on the go.", icon: "ri-wifi-off-line" },
    ],
    techStack: [
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg" },
      { name: "Dart", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/dart/dart-original.svg" },
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Reviewed team workflows." },
      { title: "Architecture", desc: "Designed app and API structure." },
      { title: "UI/UX", desc: "Built a polished mobile interface." },
      { title: "Development", desc: "Built Flutter app and Laravel API." },
      { title: "Testing", desc: "Cross-device QA." },
      { title: "Deployment", desc: "Published to both stores." },
      { title: "Support", desc: "Ongoing improvements." },
    ],
    results: [
      { title: "Faster Launch", desc: "One codebase for two platforms.", icon: "ri-rocket-line" },
      { title: "Lower Cost", desc: "Shared development effort.", icon: "ri-money-cny-circle-line" },
      { title: "Consistent UX", desc: "Identical everywhere.", icon: "ri-user-smile-line" },
      { title: "Scalable", desc: "Grows with the business.", icon: "ri-expand-diagonal-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=Mobile%20business%20app%20with%20dashboard%20and%20charts%2C%20clean%20professional%20mobile%20app%20UI%2C%20blue%20and%20cyan%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-mobile-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Mobile%20business%20app%20with%20notifications%20and%20tasks%2C%20clean%20professional%20mobile%20app%20UI%2C%20blue%20and%20cyan%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-mobile-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Mobile%20business%20app%20with%20profile%20and%20settings%2C%20clean%20professional%20mobile%20app%20UI%2C%20blue%20and%20cyan%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-mobile-3&orientation=landscape",
    ],
  },
  {
    slug: "ai-customer-support-assistant",
    title: "AI Customer Support Assistant",
    industry: "AI",
    badge: "Case Study · AI Assistant",
    heroImage: "https://readdy.ai/api/search-image?query=AI%20customer%20support%20assistant%20interface%2C%20chat%20with%20automated%20reply%20suggestions%20and%20ticket%20queue%2C%20intelligent%20helpdesk%20software%20mockup%2C%20violet%20and%20magenta%20gradient%20lighting%2C%20premium%20AI%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-ai-support&orientation=landscape",
    overview:
      "A growing service business wanted to respond to customers faster without adding more support staff. AstroJal built an AI-powered support assistant that automates replies, triages tickets and learns from every conversation.",
    challenge:
      "High support volume meant slow response times and an overwhelmed team. They needed a system that could answer common questions instantly, route complex cases to the right people and improve over time without sacrificing quality.",
    goals: [
      "Automate responses to common questions",
      "Triage and route support tickets",
      "Learn from customer conversations",
      "Integrate with the existing helpdesk",
      "Keep responses accurate and on-brand",
    ],
    solution:
      "We built a Laravel backend with an AI layer that generates on-brand replies and classifies ticket intent. Redis powers fast caching and queueing, while the assistant improves continuously from real conversations routed through a secure AWS deployment.",
    architecture: [
      { name: "Support Interface", desc: "Web & inbox", icon: "ri-window-line" },
      { name: "Laravel API", desc: "Ticket & logic", icon: "ri-code-s-slash-line" },
      { name: "AI Assistant", desc: "Reply generation", icon: "ri-bard-line" },
      { name: "Intent Engine", desc: "Ticket triage", icon: "ri-filter-3-line" },
      { name: "Redis", desc: "Cache & queues", icon: "ri-stack-line" },
      { name: "AWS", desc: "Secure cloud", icon: "ri-cloud-line" },
    ],
    keyFeatures: [
      { title: "Instant Replies", desc: "Automated on-brand responses.", icon: "ri-chat-3-line" },
      { title: "Smart Triage", desc: "Routes tickets automatically.", icon: "ri-filter-3-line" },
      { title: "Continuous Learning", desc: "Improves from conversations.", icon: "ri-brain-line" },
      { title: "Helpdesk Integration", desc: "Connects to existing tools.", icon: "ri-links-line" },
      { title: "Human Handoff", desc: "Escalates complex cases.", icon: "ri-user-line" },
      { title: "Insight Reports", desc: "Track support performance.", icon: "ri-bar-chart-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "ChatGPT", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/openai.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Mapped common support queries." },
      { title: "Architecture", desc: "Designed the AI reply and triage flow." },
      { title: "UI/UX", desc: "Built a clean support interface." },
      { title: "Development", desc: "Trained and integrated the assistant." },
      { title: "Testing", desc: "Validated accuracy and safety." },
      { title: "Deployment", desc: "Rolled out on AWS." },
      { title: "Support", desc: "Ongoing refinement." },
    ],
    results: [
      { title: "Faster Responses", desc: "Instant automated replies.", icon: "ri-flashlight-line" },
      { title: "Less Manual Work", desc: "Automated ticket triage.", icon: "ri-settings-3-line" },
      { title: "Better Consistency", desc: "On-brand support every time.", icon: "ri-check-double-line" },
      { title: "Scalable", desc: "Handles more volume.", icon: "ri-expand-diagonal-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=AI%20support%20chat%20with%20automated%20replies%20and%20suggested%20answers%2C%20clean%20professional%20helpdesk%20UI%2C%20violet%20and%20magenta%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-ai-support-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=AI%20ticket%20triage%20panel%20with%20priority%20routing%20and%20queues%2C%20clean%20professional%20helpdesk%20UI%2C%20violet%20and%20magenta%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-ai-support-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=AI%20support%20analytics%20dashboard%20with%20response%20metrics%2C%20clean%20professional%20helpdesk%20UI%2C%20violet%20and%20magenta%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-ai-support-3&orientation=landscape",
    ],
  },
  {
    slug: "business-crm-platform",
    title: "Business CRM Platform",
    industry: "SaaS",
    badge: "Case Study · CRM",
    heroImage: "https://readdy.ai/api/search-image?query=CRM%20platform%20dashboard%20UI%2C%20sales%20pipeline%20kanban%20board%20with%20lead%20cards%20and%20contact%20profiles%2C%20professional%20business%20software%20mockup%2C%20teal%20and%20orange%20gradient%20lighting%2C%20premium%20SaaS%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-crm&orientation=landscape",
    overview:
      "A sales-led business needed a clear way to manage leads, contacts and pipelines. AstroJal built a flexible CRM with automated follow-ups and sales analytics that fits the way the team actually works.",
    challenge:
      "Leads lived in spreadsheets and inboxes, so follow-ups were missed and reporting was manual. They needed a simple, reliable CRM that any team member could use, with automation to keep every lead moving forward.",
    goals: [
      "Centralize leads and contacts",
      "Visualize the sales pipeline",
      "Automate follow-up reminders",
      "Deliver sales analytics",
      "Keep the interface simple to use",
    ],
    solution:
      "We built a Laravel CRM with a Vue.js interface. A drag-friendly pipeline tracks deals, automated reminders keep follow-ups on schedule, and real-time analytics give leadership a clear view of sales performance.",
    architecture: [
      { name: "Frontend", desc: "Vue.js interface", icon: "ri-window-line" },
      { name: "Laravel API", desc: "CRM & logic", icon: "ri-code-s-slash-line" },
      { name: "Pipeline Engine", desc: "Deal tracking", icon: "ri-route-line" },
      { name: "Automation", desc: "Reminders & tasks", icon: "ri-settings-3-line" },
      { name: "Redis", desc: "Cache & queues", icon: "ri-stack-line" },
      { name: "Database", desc: "MySQL", icon: "ri-database-2-line" },
    ],
    keyFeatures: [
      { title: "Pipeline Board", desc: "Drag-friendly deal tracking.", icon: "ri-route-line" },
      { title: "Contact Hub", desc: "All leads in one place.", icon: "ri-contacts-book-line" },
      { title: "Automation", desc: "Smart follow-up reminders.", icon: "ri-settings-3-line" },
      { title: "Sales Analytics", desc: "Live performance insight.", icon: "ri-bar-chart-line" },
      { title: "Role-Based Access", desc: "Secure team permissions.", icon: "ri-shield-check-line" },
      { title: "Clean UI", desc: "Simple for everyone.", icon: "ri-palette-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Mapped the sales workflow." },
      { title: "Architecture", desc: "Designed the pipeline model." },
      { title: "UI/UX", desc: "Built a simple, clean interface." },
      { title: "Development", desc: "Built pipeline and automation." },
      { title: "Testing", desc: "Validated workflow and UX." },
      { title: "Deployment", desc: "Rolled out across the team." },
      { title: "Support", desc: "Ongoing enhancements." },
    ],
    results: [
      { title: "No Missed Leads", desc: "Automated follow-ups.", icon: "ri-check-double-line" },
      { title: "Clear Pipeline", desc: "Better deal visibility.", icon: "ri-route-line" },
      { title: "Data-Driven", desc: "Live sales reporting.", icon: "ri-bar-chart-line" },
      { title: "Easy Adoption", desc: "Simple, clean UI.", icon: "ri-user-smile-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=CRM%20sales%20pipeline%20board%20with%20lead%20cards%2C%20clean%20professional%20CRM%20software%20UI%2C%20teal%20and%20orange%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-crm-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=CRM%20contact%20profile%20with%20activity%20timeline%2C%20clean%20professional%20CRM%20software%20UI%2C%20teal%20and%20orange%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-crm-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=CRM%20sales%20analytics%20dashboard%20with%20pipeline%20charts%2C%20clean%20professional%20CRM%20software%20UI%2C%20teal%20and%20orange%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-crm-3&orientation=landscape",
    ],
  },
  {
    slug: "api-integration-platform",
    title: "API Integration Platform",
    industry: "API",
    badge: "Case Study · API Platform",
    heroImage: "https://readdy.ai/api/search-image?query=API%20integration%20platform%20UI%2C%20endpoint%20explorer%20with%20request%20and%20response%20panels%20and%20connection%20flow%20diagram%2C%20developer%20tools%20software%20mockup%2C%20blue%20and%20violet%20gradient%20lighting%2C%20premium%20developer%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-api&orientation=landscape",
    overview:
      "A technology team needed to build, secure and monitor APIs while connecting many third-party services. AstroJal delivered an API integration platform that makes development faster and operations more reliable.",
    challenge:
      "The team juggled dozens of integrations with limited visibility, leading to errors and slow debugging. They needed a unified platform to design, secure, monitor and connect APIs in one place.",
    goals: [
      "Speed up API development",
      "Centralize integrations",
      "Enforce security and rate limits",
      "Provide clear monitoring",
      "Improve developer experience",
    ],
    solution:
      "We built a Laravel-based integration platform with an interactive endpoint explorer, role-based security, rate limiting and live monitoring. Containerized services keep deployment consistent across environments.",
    architecture: [
      { name: "Developer UI", desc: "Endpoint explorer", icon: "ri-window-line" },
      { name: "Laravel API", desc: "Core gateway", icon: "ri-code-s-slash-line" },
      { name: "Integration Layer", desc: "Third-party connectors", icon: "ri-links-line" },
      { name: "Security Layer", desc: "Auth & rate limits", icon: "ri-shield-check-line" },
      { name: "Database", desc: "PostgreSQL", icon: "ri-database-2-line" },
      { name: "Containerized", desc: "Docker & cloud", icon: "ri-box-3-line" },
    ],
    keyFeatures: [
      { title: "Endpoint Explorer", desc: "Test and document APIs.", icon: "ri-code-s-slash-line" },
      { title: "Connectors", desc: "Prebuilt integrations.", icon: "ri-links-line" },
      { title: "Security", desc: "Auth and rate limits.", icon: "ri-shield-check-line" },
      { title: "Monitoring", desc: "Live request tracking.", icon: "ri-pulse-line" },
      { title: "Documentation", desc: "Auto-generated docs.", icon: "ri-file-text-line" },
      { title: "Containerized", desc: "Consistent deployment.", icon: "ri-box-3-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
      { name: "Nginx", icon: "ri-server-line" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Reviewed integration needs." },
      { title: "Architecture", desc: "Designed the API gateway." },
      { title: "UI/UX", desc: "Built the developer interface." },
      { title: "Development", desc: "Built connectors and security." },
      { title: "Testing", desc: "Endpoint and load testing." },
      { title: "Deployment", desc: "Containerized rollout." },
      { title: "Support", desc: "Ongoing monitoring." },
    ],
    results: [
      { title: "Faster Delivery", desc: "Streamlined API development.", icon: "ri-rocket-line" },
      { title: "Better Reliability", desc: "Clear monitoring and logs.", icon: "ri-pulse-line" },
      { title: "Stronger Security", desc: "Centralized controls.", icon: "ri-shield-check-line" },
      { title: "Better DX", desc: "Developer-friendly tools.", icon: "ri-user-smile-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=API%20endpoint%20explorer%20with%20request%20and%20response%20panels%2C%20clean%20professional%20developer%20tool%20UI%2C%20blue%20and%20violet%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-api-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=API%20integration%20flow%20diagram%20with%20connectors%2C%20clean%20professional%20developer%20tool%20UI%2C%20blue%20and%20violet%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-api-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=API%20monitoring%20dashboard%20with%20request%20metrics%20and%20logs%2C%20clean%20professional%20developer%20tool%20UI%2C%20blue%20and%20violet%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-api-3&orientation=landscape",
    ],
  },
  {
    slug: "enterprise-analytics-dashboard",
    title: "Enterprise Analytics Dashboard",
    industry: "Enterprise",
    badge: "Case Study · Analytics",
    heroImage: "https://readdy.ai/api/search-image?query=Enterprise%20analytics%20dashboard%20UI%2C%20interactive%20KPI%20cards%20and%20business%20intelligence%20charts%2C%20executive%20reporting%20software%20mockup%2C%20navy%20and%20gold%20gradient%20lighting%2C%20premium%20enterprise%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=cs-hero-analytics&orientation=landscape",
    overview:
      "Leadership teams needed one clear view of business performance. AstroJal built a real-time analytics dashboard with interactive charts, reports and KPI monitoring tailored to every role.",
    challenge:
      "Data was scattered across reports and spreadsheets, so decisions lagged behind reality. They needed a single dashboard that refreshed in real time, served different teams and stayed fast at scale.",
    goals: [
      "Unify data into one dashboard",
      "Deliver real-time KPI monitoring",
      "Provide role-based reporting",
      "Handle large data volumes",
      "Make data easy to understand",
    ],
    solution:
      "We built a React analytics dashboard on a Laravel API with PostgreSQL and Redis caching. Interactive charts, custom reports and role-based views give every team the insight they need, fast.",
    architecture: [
      { name: "Dashboard", desc: "React frontend", icon: "ri-window-line" },
      { name: "Laravel API", desc: "Reporting & logic", icon: "ri-code-s-slash-line" },
      { name: "Redis", desc: "Caching & speed", icon: "ri-stack-line" },
      { name: "Database", desc: "PostgreSQL", icon: "ri-database-2-line" },
      { name: "Security Layer", desc: "Role-based access", icon: "ri-shield-check-line" },
      { name: "AWS", desc: "Scalable cloud", icon: "ri-cloud-line" },
    ],
    keyFeatures: [
      { title: "Interactive Charts", desc: "Explore data in real time.", icon: "ri-line-chart-line" },
      { title: "KPI Monitoring", desc: "Track key metrics live.", icon: "ri-dashboard-line" },
      { title: "Custom Reports", desc: "Build the reports you need.", icon: "ri-file-chart-line" },
      { title: "Role-Based Views", desc: "Right data for every team.", icon: "ri-user-line" },
      { title: "Fast at Scale", desc: "Handles large datasets.", icon: "ri-speed-line" },
      { title: "Data Security", desc: "Protected reporting.", icon: "ri-shield-check-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    process: [
      { title: "Discovery", desc: "Identified key metrics per role." },
      { title: "Architecture", desc: "Designed the reporting model." },
      { title: "UI/UX", desc: "Built clear data views." },
      { title: "Development", desc: "Built charts and reports." },
      { title: "Testing", desc: "Performance and accuracy QA." },
      { title: "Deployment", desc: "Scalable production rollout." },
      { title: "Support", desc: "Ongoing enhancements." },
    ],
    results: [
      { title: "Real-time Insight", desc: "Live KPI monitoring.", icon: "ri-line-chart-line" },
      { title: "Better Decisions", desc: "Data-driven leadership.", icon: "ri-lightbulb-line" },
      { title: "Team-Specific", desc: "Role-based reporting.", icon: "ri-user-line" },
      { title: "Fast at Scale", desc: "Handles large volumes.", icon: "ri-speed-line" },
    ],
    screenshots: [
      "https://readdy.ai/api/search-image?query=Enterprise%20analytics%20dashboard%20with%20KPI%20cards%20and%20charts%2C%20clean%20professional%20BI%20software%20UI%2C%20navy%20and%20gold%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-analytics-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Enterprise%20BI%20reporting%20interface%20with%20interactive%20charts%2C%20clean%20professional%20BI%20software%20UI%2C%20navy%20and%20gold%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-analytics-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Enterprise%20analytics%20executive%20overview%20dashboard%2C%20clean%20professional%20BI%20software%20UI%2C%20navy%20and%20gold%20gradient%20lighting%2C%20no%20text%2C%20no%20people&width=800&height=520&seq=cs-shot-analytics-3&orientation=landscape",
    ],
  },
];